/**
 * QuizReview (Sprint 6 B4) — sau submit xem điểm.
 *
 * Route: /quizzes/attempts/:attemptId/review
 * UX:
 *   - Header: tổng điểm + % + tag passed/failed
 *   - List question với badge correct/wrong + show correct choice
 *   - Link "Về khóa của tôi"
 */

import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  List,
  Progress,
  Result,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { Link, useParams } from "react-router-dom";
import { MarkdownContent } from "@/components/MarkdownContent";
import { pctTo10 } from "@/components/ScoreTag";
import { scorePercent, useAttempt } from "@/api/assessment";
import { formatDateTime } from "@/lib/formatDate";

const { Title, Text, Paragraph } = Typography;

export function QuizReview() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const aid = attemptId ? Number(attemptId) : undefined;
  const { data: attempt, isLoading, error } = useAttempt(aid);

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: 64 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (error || !attempt) {
    return (
      <Result
        status="404"
        title="Không tìm thấy bài làm"
        extra={<Link to="/my-courses">← Về môn học của tôi</Link>}
      />
    );
  }
  if (attempt.status === "in_progress") {
    return (
      <Alert
        type="info"
        showIcon
        message="Bài thi đang dang dở"
        description={<Link to={`/quizzes/attempts/${attempt.id}/take`}>← Quay lại làm bài</Link>}
      />
    );
  }

  const pct = scorePercent(attempt);
  const passed = attempt.passed;
  const questions = [...attempt.questions].sort((a, b) => a.order - b.order);

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }} data-testid="quiz-review">
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/my-courses">Khóa của tôi</Link> },
          { title: attempt.quiz.title },
          { title: "Kết quả" },
        ]}
      />

      <Card style={{ marginBottom: 24 }}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Title level={2} style={{ margin: 0 }}>
            <TrophyOutlined /> {attempt.quiz.title} — Kết quả
          </Title>
          <Space size="large" wrap>
            <Text strong style={{ fontSize: 24 }}>
              {attempt.score} / {attempt.total_points} điểm
            </Text>
            <Progress
              type="circle"
              percent={pct}
              size={80}
              status={passed ? "success" : "exception"}
              format={() => pctTo10(pct).toFixed(1)}
            />
            {passed ? (
              <Tag color="success" icon={<CheckCircleOutlined />} style={{ fontSize: 16 }}>
                ĐẠT ({attempt.quiz.pass_threshold_pct}%+)
              </Tag>
            ) : (
              <Tag color="error" icon={<CloseCircleOutlined />} style={{ fontSize: 16 }}>
                KHÔNG ĐẠT (cần ≥{attempt.quiz.pass_threshold_pct}%)
              </Tag>
            )}
          </Space>
          {attempt.needs_manual_grading && (
            <Alert
              type="warning"
              showIcon
              icon={<ClockCircleOutlined />}
              message="Còn câu tự luận/trả lời ngắn chưa được giảng viên chấm — điểm trên chưa phải điểm cuối cùng."
            />
          )}
          <Text type="secondary">Nộp lúc: {formatDateTime(attempt.submitted_at)}</Text>
        </Space>
      </Card>

      <Title level={3}>Chi tiết từng câu</Title>
      <List
        dataSource={questions}
        renderItem={(q, idx) => {
          const ansSnap = attempt.answers[String(q.id)];
          const isTextQuestion = q.type === "short_answer" || q.type === "essay";

          if (isTextQuestion) {
            const graded = ansSnap?.manually_graded === true;
            return (
              <List.Item>
                <Card
                  style={{ width: "100%" }}
                  title={
                    <Space>
                      <Text strong>Câu {idx + 1}</Text>
                      {graded ? (
                        <Tag color="success" icon={<CheckCircleOutlined />}>
                          Đã chấm (+{ansSnap?.points_awarded ?? 0} điểm)
                        </Tag>
                      ) : (
                        <Tag color="warning" icon={<ClockCircleOutlined />}>
                          Đang chờ chấm
                        </Tag>
                      )}
                    </Space>
                  }
                >
                  <MarkdownContent source={q.prompt} />
                  <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 4 }}>
                    Câu trả lời của bạn:
                  </Paragraph>
                  <Paragraph style={{ whiteSpace: "pre-line" }}>
                    {ansSnap?.text_answer || "(chưa trả lời)"}
                  </Paragraph>
                  {graded && ansSnap?.feedback && (
                    <>
                      <Paragraph type="secondary" style={{ marginBottom: 4 }}>
                        Nhận xét của giảng viên:
                      </Paragraph>
                      <Paragraph>{ansSnap.feedback}</Paragraph>
                    </>
                  )}
                </Card>
              </List.Item>
            );
          }

          const isCorrect = ansSnap?.is_correct === true;
          const selectedIds = new Set(ansSnap?.selected_choice_ids ?? []);
          return (
            <List.Item>
              <Card
                style={{ width: "100%" }}
                title={
                  <Space>
                    <Text strong>Câu {idx + 1}</Text>
                    {isCorrect ? (
                      <Tag color="success" icon={<CheckCircleOutlined />}>
                        Đúng (+{ansSnap?.points_awarded ?? 0} điểm)
                      </Tag>
                    ) : (
                      <Tag color="error" icon={<CloseCircleOutlined />}>
                        Sai (0 điểm)
                      </Tag>
                    )}
                  </Space>
                }
              >
                <MarkdownContent source={q.prompt} />
                <Paragraph type="secondary" style={{ marginTop: 12 }}>
                  Đáp án của bạn:
                </Paragraph>
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  {[...q.choices]
                    .sort((a, b) => a.order - b.order)
                    .map((c) => {
                      const userPicked = selectedIds.has(c.id);
                      const isRight = c.is_correct === true;
                      let bg = "transparent";
                      if (isRight) bg = "#f6ffed";
                      else if (userPicked && !isRight) bg = "#fff1f0";
                      return (
                        <div
                          key={c.id}
                          style={{
                            padding: "6px 10px",
                            background: bg,
                            borderRadius: 4,
                          }}
                        >
                          <Space>
                            {userPicked && <Tag>Bạn chọn</Tag>}
                            {isRight && <Tag color="green">Đáp án đúng</Tag>}
                            <Text>{c.text}</Text>
                          </Space>
                        </div>
                      );
                    })}
                </Space>
              </Card>
            </List.Item>
          );
        }}
      />

      <div style={{ textAlign: "center", marginTop: 24 }}>
        <Link to="/my-courses">
          <Button type="primary" size="large">
            ← Về khóa của tôi
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default QuizReview;
